import type { TaskCreateBody } from "@api/task.api";
import type { TaskListQuery } from "@common/contracts/task.contract";
import {
    type Task,
    TaskGroupBy,
    TaskPriority,
    TaskStatus,
    TaskType,
} from "@common/types/task";
import { NEW_TASK_PLACEHOLDER_WORKSPACE_ID } from "./taskListConstants";
import {
    DueDateGroup,
    getDefaultDueDateForGroup,
    NO_ASSIGNEE_KEY,
    NO_CLOSE_KEY,
    NO_CLOSE_STAGE_KEY,
    NO_PRIORITY_KEY,
    NO_TASK_GROUP_KEY,
} from "./taskListUtils";

/** The filters a new task picks values from when its group doesn't set them. */
export type TaskPlaceholderFilters = Pick<
    TaskListQuery,
    "assigneeIds" | "closeIds" | "entityIds"
>;

interface TaskPlaceholderFieldsArgs {
    groupKey: string;
    groupBy: TaskGroupBy;
    isGroupedByCloseStage: boolean;
    /** A task already in the group, whose values the new one copies. */
    sampleTask: Task | undefined;
    filters: TaskPlaceholderFilters;
}

const findValue = <T extends string>(values: readonly T[], key: string) =>
    values.find((value) => value === key);

const orNull = (key: string, noneKey: string) => (key === noneKey ? null : key);

const getOnlyValue = <T>(values: readonly T[] | undefined) =>
    values?.length === 1 ? (values[0] ?? null) : null;

const parsePriorityKey = (key: string) =>
    key === NO_PRIORITY_KEY
        ? null
        : (findValue(Object.values(TaskPriority), key) ?? null);

const parseAssigneeKey = (key: string) =>
    key === NO_ASSIGNEE_KEY ? null : parseInt(key, 10);

/**
 * The values a task created in a List group (or a Board column) starts
 * with: the group's own value, a sample task's values for some fields, the
 * single filtered assignee, service and the filtered entities.
 */
export const getPlaceholderTaskFields = ({
    groupKey,
    groupBy,
    isGroupedByCloseStage,
    sampleTask,
    filters,
}: TaskPlaceholderFieldsArgs) => ({
    priority:
        groupBy === TaskGroupBy.PRIORITY
            ? (sampleTask?.priority ?? parsePriorityKey(groupKey))
            : null,
    assigneeId:
        groupBy === TaskGroupBy.ASSIGNEE
            ? (sampleTask?.assigneeId ?? parseAssigneeKey(groupKey))
            : getOnlyValue(filters.assigneeIds),
    status:
        groupBy === TaskGroupBy.STATUS
            ? (sampleTask?.status ??
              findValue(Object.values(TaskStatus), groupKey) ??
              TaskStatus.TODO)
            : TaskStatus.TODO,
    groupId:
        groupBy === TaskGroupBy.TASK_GROUP
            ? (sampleTask?.groupId ?? orNull(groupKey, NO_TASK_GROUP_KEY))
            : (sampleTask?.groupId ?? null),
    closeStageId: isGroupedByCloseStage
        ? (sampleTask?.closeStageId ?? orNull(groupKey, NO_CLOSE_STAGE_KEY))
        : (sampleTask?.closeStageId ?? null),
    closeId:
        groupBy === TaskGroupBy.CLOSE
            ? (sampleTask?.closeId ?? orNull(groupKey, NO_CLOSE_KEY))
            : (sampleTask?.closeId ?? getOnlyValue(filters.closeIds)),
    entityIds: filters.entityIds?.length ? filters.entityIds : undefined,
    dueDate:
        groupBy === TaskGroupBy.DUE_DATE
            ? getDefaultDueDateForGroup(
                  findValue(Object.values(DueDateGroup), groupKey) ??
                      DueDateGroup.NO_DUE_DATE,
              )
            : null,
});

/** What a new task in a List group or Board column starts with. */
export type TaskPlaceholderFields = ReturnType<typeof getPlaceholderTaskFields>;

/**
 * What List's new-task row and Board's draft card send to create a task: a
 * question titled `title` with the draft's `fields`.
 */
export const toTaskCreateBody = ({
    title,
    fields,
}: {
    title: string;
    fields: Pick<
        TaskCreateBody,
        | "assigneeId"
        | "priority"
        | "status"
        | "dueDate"
        | "groupId"
        | "closeStageId"
        | "closeId"
    > & { entityIds?: number[] | null };
}): TaskCreateBody => ({
    title,
    type: TaskType.QUESTION,
    assigneeId: fields.assigneeId,
    priority: fields.priority,
    status: fields.status,
    dueDate: fields.dueDate,
    groupId: fields.groupId,
    closeStageId: fields.closeStageId,
    closeId: fields.closeId,
    entityIds: fields.entityIds ?? undefined,
});

/**
 * A task that isn't saved yet, for List's new-task row and Board's draft
 * card: an untitled question with `fields`, in no workspace.
 */
export const buildUnsavedTask = ({
    id,
    fields,
}: {
    id: string;
    fields: TaskPlaceholderFields;
}): Task => ({
    ...fields,
    id,
    workspaceId: NEW_TASK_PLACEHOLDER_WORKSPACE_ID,
    organizationId: null,
    title: "",
    description: null,
    type: TaskType.QUESTION,
    createdById: null,
    createdAt: new Date(),
    updatedAt: new Date(),
});
